
import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ConvertDate from "../helpers/DateConverter";
import { loadConfig } from "../helpers/config";
import LoadingSpinner from "../components/LoadingSpinner";
import { AuthContext } from "../helpers/AuthContext";

// Component to change owner
function ChangeOwner({ tool, apiUrl, refreshTool }) {
  const [editing, setEditing] = React.useState(false);
  const [owner, setOwner] = React.useState(tool.owner || "");
  const [saving, setSaving] = React.useState(false);
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/tool/${tool.id}`, { owner }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      if (typeof refreshTool === "function") {
        await refreshTool();
      }
      setEditing(false);
    } catch (e) {
      alert("Fehler beim Speichern des Eigentümers.");
    }
    setSaving(false);
  };
  if (!editing) {
    return <button className="btn btn-link btn-sm" onClick={() => setEditing(true)} style={{ marginLeft: 8 }}>ändern</button>;
  }
  return (
    <span style={{ marginLeft: 8 }}>
      <input
        type="text"
        value={owner}
        onChange={e => setOwner(e.target.value)}
        className="form-control"
        style={{ display: "inline-block", width: "auto", minWidth: 120 }}
        placeholder="Eigentümer"
      />
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ marginLeft: 4 }}>Speichern</button>
      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving} style={{ marginLeft: 4 }}>Abbrechen</button>
    </span>
  );
}

// Component to change storage position
function ChangeStoragePosition({ tool, apiUrl, refreshTool }) {

  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState(tool.storagePosition || "");
  const [saving, setSaving] = useState(false);
  let optionsRaw = tool.ToolsGroup?.storagePositions;
  let options = [];
  if (typeof optionsRaw === "string") {
    try {
      options = JSON.parse(optionsRaw);
    } catch (e) {
      options = [];
    }
  } else if (Array.isArray(optionsRaw)) {
    options = optionsRaw;
  }
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/tool/${tool.id}`, { storagePosition: selected }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      if (typeof refreshTool === "function") {
        await refreshTool();
      }
      setEditing(false);
    } catch (e) {
      alert("Fehler beim Speichern des Lagerorts.");
    }
    setSaving(false);
  };
  if (!editing) {
    return <button className="btn btn-link btn-sm" onClick={() => setEditing(true)} style={{ marginLeft: 8 }}>ändern</button>;
  }
  return (
    <span style={{ marginLeft: 8 }}>
      <select value={selected} onChange={e => setSelected(e.target.value)} className="form-control" style={{ display: "inline-block", width: "auto", minWidth: 120 }}>
        <option value="">Bitte wählen</option>
        {[...options].sort((a, b) => a.localeCompare(b)).map((opt, idx) => (
          <option key={idx} value={opt}>{opt}</option>
        ))}
      </select>
      <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving} style={{ marginLeft: 4 }}>Speichern</button>
      <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving} style={{ marginLeft: 4 }}>Abbrechen</button>
    </span>
  );
}

// Component to toggle discarded status (admin only)
function ToggleDiscarded({ tool, apiUrl, refreshTool, isAdmin }) {
  const [saving, setSaving] = useState(false);
  const handleToggle = async () => {
    const action = tool.isDiscarded ? "wieder in Betrieb nehmen" : "aussondern";
    if (!window.confirm(`Möchten Sie dieses Werkzeug wirklich ${action}?`)) {
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${apiUrl}/tool/${tool.id}`, { isDiscarded: !tool.isDiscarded }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      if (typeof refreshTool === "function") {
        await refreshTool();
      }
    } catch (e) {
      const errorMsg = e.response?.data?.error || "Fehler beim Ändern des Status.";
      alert(errorMsg);
    }
    setSaving(false);
  };
  if (!isAdmin) return null;
  return (
    <button 
      className={tool.isDiscarded ? "btn btn-success btn-sm" : "btn btn-warning btn-sm"} 
      onClick={handleToggle} 
      disabled={saving}
      style={{ marginLeft: 8 }}
    >
      {tool.isDiscarded ? "Wieder in Betrieb nehmen" : "Aussondern"}
    </button>
  );
}

// Component to delete tool (admin only, only if no checks or wash entries exist)
function DeleteTool({ tool, apiUrl, isAdmin, checksCount, washLogsCount, navigate }) {
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (!window.confirm(`Möchten Sie dieses Werkzeug wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${apiUrl}/tool/${tool.id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      alert("Werkzeug erfolgreich gelöscht");
      navigate("/Tools");
    } catch (e) {
      const errorMsg = e.response?.data?.error || "Fehler beim Löschen des Werkzeugs.";
      alert(errorMsg);
      setDeleting(false);
    }
  };
  if (!isAdmin || checksCount > 0 || washLogsCount > 0) return null;
  return (
    <button 
      className="btn btn-danger btn-sm" 
      onClick={handleDelete} 
      disabled={deleting}
      style={{ marginLeft: 8 }}
    >
      Werkzeug löschen
    </button>
  );
}

function Tool() {
  const { authState } = useContext(AuthContext);
  // Function to refetch tool data after position change
  const refreshTool = async () => {
    if (!loadingConfig) {
      const response = await axios.get(`${apiUrl}/tool/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      setToolObject(response.data);
    }
  };
  let { id } = useParams();
  let navigate = useNavigate();
  const [ToolObject, setToolObject] = useState({});
  const [ChecksObject, setChecksObject] = useState([]);
  // Remove single NextCheckObject, use per-category next check calculation
  const [apiUrl, setApiUrl] = useState("");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [logbook, setLogbook] = useState([]);
  const [categories, setCategories] = useState([]);
  // Remove selectedCategory, use all categories
  const [washLogs, setWashLogs] = useState([]);
  const [washDate, setWashDate] = useState("");
  const [washedBy, setWashedBy] = useState(localStorage.getItem("username") || "");
  const [washNotes, setWashNotes] = useState("");
  const [washImpregnated, setWashImpregnated] = useState(false);
  const [savingWash, setSavingWash] = useState(false);

  const refreshWashLogs = React.useCallback(async () => {
    if (!loadingConfig) {
      const response = await axios.get(`${apiUrl}/washlogs/byToolId/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      setWashLogs(response.data);
    }
  }, [loadingConfig, apiUrl, id]);

  const handleAddWash = async () => {
    if (!washDate) {
      alert("Bitte ein Waschdatum angeben.");
      return;
    }
    setSavingWash(true);
    try {
      await axios.post(`${apiUrl}/washlogs`, {
        toolId: id,
        washDate,
        washedBy,
        notes: washNotes,
        impregnated: washImpregnated
      }, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      setWashDate("");
      setWashNotes("");
      setWashImpregnated(false);
      await refreshWashLogs();
    } catch (e) {
      alert("Fehler beim Speichern des Wascheintrags.");
    }
    setSavingWash(false);
  };

  const handleInvalidateWash = async (washId) => {
    if (!window.confirm("Möchten Sie diesen Wascheintrag wirklich als ungültig markieren?")) {
      return;
    }
    try {
      await axios.put(`${apiUrl}/washlogs/${washId}/invalidate`, {}, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      await refreshWashLogs();
    } catch (e) {
      alert("Fehler beim Ungültigmachen des Wascheintrags.");
    }
  };

  // Handler to invalidate a check
  const handleInvalidateCheck = async (checkId) => {
    if (!window.confirm("Möchten Sie diese Prüfung wirklich als ungültig markieren?")) {
      return;
    }
    try {
      await axios.put(`${apiUrl}/check/${checkId}/invalidate`, {}, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      // Refresh checks and tool data
      const response = await axios.get(`${apiUrl}/checks/byToolId/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      });
      setChecksObject(response.data);
      await refreshTool();
    } catch (e) {
      alert("Fehler beim Ungültigmachen der Prüfung.");
    }
  };

  useEffect(() => {
    async function fetchConfig() {
      let url = await loadConfig();
      setApiUrl(url.APIURL);
      setLoadingConfig(false);
    }
    fetchConfig();
  }, []);

  useEffect(() => {
    if (!loadingConfig) {
      axios.get(`${apiUrl}/tool/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      }).then((response) => {
        setToolObject(response.data);
        // Fetch categories for the toolsgroup
        if (response.data.ToolsGroup && response.data.ToolsGroup.id) {
          axios.get(`${apiUrl}/categories/toolsgroup/${response.data.ToolsGroup.id}`, {
            headers: { accessToken: localStorage.getItem("accessToken") }
          }).then((catRes) => {
            setCategories(catRes.data);
            // No need to setSelectedCategory; use all categories for next check calculation
          });
        }
      });
    }
  }, [loadingConfig, apiUrl, id]);

  useEffect(() => {
    if (!loadingConfig) {
      axios.get(`${apiUrl}/checks/byToolId/${id}`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      }).then((response) => {
        setChecksObject(response.data);
      });
      // Fetch logbook for the tool
      axios.get(`${apiUrl}/tool/${id}/logbook`, {
        headers: { accessToken: localStorage.getItem("accessToken") }
      }).then((logRes) => {
        setLogbook(logRes.data);
      });
    }
  }, [loadingConfig, apiUrl, id]);

  useEffect(() => {
    if (ToolObject.ToolsGroup && ToolObject.ToolsGroup.isClothing) {
      refreshWashLogs();
    }
  }, [ToolObject.ToolsGroup, refreshWashLogs]);



  // Helper to calculate next check date for a category
  function getNextCheckDate(category) {
    let lastcheckdate = null;
    if (ChecksObject.length > 0) {
      for (const element of ChecksObject) {
        // If checks are associated with a category, filter by categoryId and exclude invalid checks
        if (element.checkresult === "OK" && (element.categoryId === category.id || element.CategoryId === category.id) && element.isValid !== false) {
          lastcheckdate = element.checkdate;
          break;
        }
      }
    }
    let interval = Number(category.interval) || 0;
    let unit = (category.unit || '').toLowerCase();
    function addInterval(date, interval, unit) {
      const d = new Date(date);
      if (unit === 'month' || unit === 'monate') {
        d.setMonth(d.getMonth() + interval);
      } else if (unit === 'year' || unit === 'jahr' || unit === 'jahre') {
        d.setFullYear(d.getFullYear() + interval);
      } else if (unit === 'day' || unit === 'tag' || unit === 'tage') {
        d.setDate(d.getDate() + interval);
      } else {
        d.setMonth(d.getMonth() + interval);
      }
      return d;
    }
    if (lastcheckdate && interval > 0) {
      return addInterval(lastcheckdate, interval, unit);
    } else if (ToolObject.dateofpurchase && interval > 0) {
      return addInterval(ToolObject.dateofpurchase, interval, unit);
    }
    return null;
  }

  if (loadingConfig) {
    return <LoadingSpinner text="Lade Konfiguration..." />;
  }

  if(Object.keys(ToolObject).length > 0) { 
  // Removed unused nextcheckcolor and nextcheckdate variables
    return (
      <div className="container">
      <div className="card">
        <div className="card-header">
          Gerätedetails
        </div>
        <div className="card-body">
          <table className="table table-bordered table-kv">
            <tbody>
              <tr>
                <td style={{ width: "150px" }}><strong>Seriennummer:</strong></td>
                <td colSpan={2}>{ToolObject.serialnumber}</td>
              {/* Removed old single next check row; now handled per category below */}
              </tr>
              <tr>
                <td><strong>Gerätetyp:</strong></td>
                <td colSpan={2}>{ToolObject.ToolsGroup.groupname}</td>
              </tr>
              <tr>
                <td><strong>Lagerort:</strong></td>
                <td colSpan={2}>
                  {ToolObject.storagePosition || ""}
                  {authState.isAdmin && <ChangeStoragePosition tool={ToolObject} apiUrl={apiUrl} refreshTool={refreshTool} />}
                </td>
              </tr>
              <tr>
                <td><strong>Eigentümer:</strong></td>
                <td colSpan={2}>
                  {ToolObject.owner || ""}
                  {authState.isAdmin && <ChangeOwner tool={ToolObject} apiUrl={apiUrl} refreshTool={refreshTool} />}
                </td>
              </tr>
              <tr>
                <td><strong>Status:</strong></td>
                <td colSpan={2}>
                  {ToolObject.isDiscarded ? (
                    <span style={{ color: 'gray' }}>Ausgesondert</span>
                  ) : ToolObject.isOperational === false ? (
                    <span style={{ color: 'red' }}>Defekt</span>
                  ) : (
                    <span style={{ color: 'green' }}>Betriebsbereit</span>
                  )}
                  <ToggleDiscarded tool={ToolObject} apiUrl={apiUrl} refreshTool={refreshTool} isAdmin={authState.isAdmin} />
                  <DeleteTool tool={ToolObject} apiUrl={apiUrl} isAdmin={authState.isAdmin} checksCount={ChecksObject.length} washLogsCount={washLogs.length} navigate={navigate} />
                </td>
              </tr>
              {/* List all categories and their next check dates */}
              {categories.length > 0 && categories.map(cat => {
                const nextDate = getNextCheckDate(cat);
                // Find all valid checks for this category
                const checksForCategory = ChecksObject.filter(c => (c.categoryId === cat.id || c.CategoryId === cat.id) && c.isValid !== false);
                // If no checks, use purchase date as overdue date
                let displayDate = nextDate;
                if (checksForCategory.length === 0 && ToolObject.dateofpurchase) {
                  displayDate = new Date(ToolObject.dateofpurchase);
                }
                // Overdue: no checks for this category, or displayDate in the past
                const isOverdue = checksForCategory.length === 0 || !displayDate || Date.parse(displayDate) < Date.now();
                const nextColor = isOverdue ? "red" : "green";
                // Find unfinished check for this category
                const unfinished = ChecksObject.find(c => c.finished === false && c.categoryId === cat.id);
                return (
                  <tr key={cat.id}>
                    <td>
                      <strong>Prüfkategorie:</strong> {cat.name} ({cat.interval} {cat.unit})
                    </td>
                    <td style={{ color: nextColor }}>{displayDate ? displayDate.toLocaleDateString() : "-"}</td>
                    <td>
                      {ToolObject.isDiscarded ? (
                        <span style={{ color: 'gray', fontStyle: 'italic' }}>Werkzeug ausgesondert</span>
                      ) : unfinished ? (
                        <Link
                          to={`/Checks/Add/${id}`}
                          className="btn btn-warning btn-sm"
                          style={{ marginRight: "10px" }}
                          state={{ checkId: unfinished.id, categoryId: cat.id }}
                        >
                          Prüfung fortsetzen
                        </Link>
                      ) : (
                        <Link
                          to={`/Checks/Add/${id}`}
                          className="btn btn-primary btn-sm"
                          style={{ marginRight: "10px" }}
                          state={{ categoryId: cat.id }}
                        >
                          Prüfung starten
                        </Link>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-header">Prüfhistorie</div>
        <div className="card-body">
          <table className="table table-stack">
            <thead>
              <tr>
                <th>Prüfdatum</th>
                <th>Prüfkategorie</th>
                <th>Prüfergebnis</th>
                <th>Prüfer</th>
                <th>Status</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {ChecksObject.length > 0 ? (
                ChecksObject.map((value, key) => {
                  // Find category name for this check
                  const cat = categories.find(c => c.id === value.categoryId);
                  const isInvalid = value.isValid === false;
                  return (
                    <tr
                      key={key}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: isInvalid ? '#f5f5f5' : 'transparent',
                        textDecoration: isInvalid ? 'line-through' : 'none',
                        color: isInvalid ? '#999' : 'inherit'
                      }}
                    >
                      <td data-label="Prüfdatum" onClick={() => navigate(`/Check/${value.id}`)}>{ConvertDate(value.checkdate)}</td>
                      <td data-label="Prüfkategorie" onClick={() => navigate(`/Check/${value.id}`)}>{cat ? cat.name : '-'}</td>
                      <td data-label="Prüfergebnis" onClick={() => navigate(`/Check/${value.id}`)}>{value.checkresult}</td>
                      <td data-label="Prüfer" onClick={() => navigate(`/Check/${value.id}`)}>{value.inspector}</td>
                      <td data-label="Status" onClick={() => navigate(`/Check/${value.id}`)}>
                        {isInvalid ? (
                          <span style={{ color: 'red', fontStyle: 'italic' }}>Ungültig</span>
                        ) : (
                          <span style={{ color: 'green' }}>Gültig</span>
                        )}
                      </td>
                      <td data-label="Aktionen">
                        {authState.isAdmin && !isInvalid && value.finished && (
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleInvalidateCheck(value.id);
                            }}
                          >
                            Ungültig machen
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">Keine Prüfungen für dieses Gerät vorhanden</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {ToolObject.ToolsGroup && ToolObject.ToolsGroup.isClothing && (
        <>
        <div className="card mt-4">
          <div className="card-header">Waschhistorie</div>
          <div className="card-body">
            <p><strong>Anzahl Wäschen:</strong> {washLogs.filter(entry => entry.isValid !== false).length}</p>
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Waschdatum</th>
                  <th>Gewaschen von</th>
                  <th>Notiz</th>
                  <th>Imprägniert</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {washLogs.length > 0 ? (
                  washLogs.map((entry) => {
                    const isInvalid = entry.isValid === false;
                    return (
                      <tr
                        key={entry.id}
                        style={{
                          backgroundColor: isInvalid ? '#f5f5f5' : 'transparent',
                          textDecoration: isInvalid ? 'line-through' : 'none',
                          color: isInvalid ? '#999' : 'inherit'
                        }}
                      >
                        <td data-label="Waschdatum">{ConvertDate(entry.washDate)}</td>
                        <td data-label="Gewaschen von">{entry.washedBy}</td>
                        <td data-label="Notiz">{entry.notes}</td>
                        <td data-label="Imprägniert">{entry.impregnated ? "Ja" : "Nein"}</td>
                        <td data-label="Status">
                          {isInvalid ? (
                            <span style={{ color: 'red', fontStyle: 'italic' }}>Ungültig</span>
                          ) : (
                            <span style={{ color: 'green' }}>Gültig</span>
                          )}
                        </td>
                        <td>
                          {authState.isAdmin && !isInvalid && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleInvalidateWash(entry.id)}
                            >
                              Ungültig machen
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">Keine Wascheinträge für dieses Gerät vorhanden</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="card mt-4">
          <div className="card-header">Wäsche hinzufügen</div>
          <div className="card-body">
            <div className="form-group">
              <label htmlFor="washDate">Waschdatum:</label>
              <input
                id="washDate"
                type="date"
                className="form-control"
                value={washDate}
                onChange={e => setWashDate(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="washedBy">Gewaschen von:</label>
              <input
                id="washedBy"
                type="text"
                className="form-control"
                value={washedBy}
                onChange={e => setWashedBy(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="washNotes">Notiz:</label>
              <input
                id="washNotes"
                type="text"
                className="form-control"
                value={washNotes}
                onChange={e => setWashNotes(e.target.value)}
              />
            </div>
            <div className="form-group form-check" style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label htmlFor="washImpregnated" className="form-check-label" style={{ marginBottom: 0 }}>Imprägniert</label>
              <input
                id="washImpregnated"
                type="checkbox"
                className="form-check-input"
                checked={washImpregnated}
                onChange={e => setWashImpregnated(e.target.checked)}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={handleAddWash}
              disabled={savingWash}
            >
              Wäsche eintragen
            </button>
          </div>
        </div>
        </>
      )}

      <div className="card mt-4">
        <div className="card-header">Logbuch</div>
        <div className="card-body">
          {logbook.length > 0 ? (
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Änderung</th>
                  <th>
                    Benutzer
                  </th>
                  <th>Zeitpunkt</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logbook.map((entry, idx) => (
                  <tr key={idx}>
                    <td data-label="Änderung">{entry.changeType}</td>
                    <td data-label="Benutzer">{entry.inspector}</td>
                    <td data-label="Zeitpunkt">{ConvertDate(entry.timestamp)}</td>
                    <td data-label="Details">
                      {(() => {
                        let detailsObj = entry.details;
                        if (typeof detailsObj === "string") {
                          try {
                            detailsObj = JSON.parse(detailsObj);
                          } catch (e) {
                            detailsObj = {};
                          }
                        }
                        return detailsObj && Object.keys(detailsObj).length > 0 ? (
                          <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                            {Object.entries(detailsObj)
                              .filter(([field, change]) => change && typeof field === "string" && isNaN(Number(field)))
                              .map(([field, change]) => {
                                // Translation map for field names
                                const fieldTranslations = {
                                  checkdate: "Prüfdatum",
                                  checkresult: "Prüfergebnis",
                                  notes: "Notizen",
                                  inspector: "Prüfer",
                                  finished: "Abgeschlossen",
                                  pdfPath: "PDF-Pfad",
                                  customChecks: "Prüfpunkte",
                                  categoryId: "Prüfkategorie",
                                  ToolId: "Werkzeug-ID",
                                  isValid: "Gültig",
                                  owner: "Eigentümer",
                                  isDiscarded: "Ausgesondert",
                                  storagePosition: "Lagerort"
                                };
                                const translateField = (fieldName) => fieldTranslations[fieldName] || fieldName;
                                // Helper to parse and format values
                                const formatValue = (val) => {
                                  if (val === undefined || val === null) return "-";
                                  if (typeof val === "boolean") return val ? "Ja" : "Nein";
                                  if (typeof val === "string") {
                                    // Try to parse JSON
                                    try {
                                      const parsed = JSON.parse(val);
                                      if (typeof parsed === "object") {
                                        return (
                                          <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                                            {Object.entries(parsed).map(([k, v]) => (
                                              <li key={k}><strong>{k}:</strong> {String(v)}</li>
                                            ))}
                                          </ul>
                                        );
                                      }
                                    } catch (e) {}
                                    // Format date
                                    if (/^\d{4}-\d{2}-\d{2}/.test(val)) {
                                      const d = new Date(val);
                                      if (!isNaN(d)) return d.toLocaleString();
                                    }
                                  }
                                  return String(val);
                                };
                                // Special rendering for customChecks
                                if (field === "customChecks" && change && typeof change === "object") {
                                  return (
                                    <li key={field}>
                                      <strong>{translateField(field)}:</strong>
                                      <ul style={{ paddingLeft: 16, marginBottom: 0 }}>
                                        {Object.entries(change).map(([customField, customChange]) => (
                                          <li key={customField}>
                                            <strong>{customField}:</strong> {customChange.from === undefined || customChange.from === null ? 
                                              <em>{formatValue(customChange.to)}</em> : 
                                              <span>von <em>{formatValue(customChange.from)}</em> zu <em>{formatValue(customChange.to)}</em></span>
                                            }
                                          </li>
                                        ))}
                                      </ul>
                                    </li>
                                  );
                                }
                                return (
                                  <li key={field}>
                                    <strong>{translateField(field)}:</strong> {change.from === undefined || change.from === null ? 
                                      <em>{formatValue(change.to)}</em> : 
                                      <span>von <em>{formatValue(change.from)}</em> zu <em>{formatValue(change.to)}</em></span>
                                    }
                                  </li>
                                );
                              })}
                          </ul>
                        ) : (
                          <span>Keine Änderungen</span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div>Keine Logbuch-Einträge vorhanden</div>
          )}
        </div>
      </div>
    </div>
    );
  } else {
    return (
      <div>Gerät nicht gefunden</div>
    )
  }
}

export default Tool;
